/**
 * ThemeSelector - Selector simple de tema (Claro, Oscuro, Sistema)
 */
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sun, Moon, Desktop, CaretDown, Check } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';

const themes = [
  { 
    id: 'light', 
    name: 'Claro', 
    description: 'Terminal de Trading',
    icon: Sun,
    color: '#00dcff'
  },
  { 
    id: 'dark', 
    name: 'Oscuro', 
    description: 'Modo oscuro',
    icon: Moon,
    color: '#6366f1'
  },
  { 
    id: 'system', 
    name: 'Sistema', 
    description: 'Automático',
    icon: Desktop,
    color: '#22c55e'
  }
];

const ThemeSelector = () => {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  const currentTheme = themes.find(t => t.id === theme) || themes[0];
  const Icon = currentTheme.icon;

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
        style={{
          background: isOpen ? 'rgba(0, 220, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${isOpen ? 'rgba(0, 220, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
        }}
        data-testid="theme-selector"
      >
        <div 
          className="w-5 h-5 rounded-full flex items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${currentTheme.color}40 0%, ${currentTheme.color}20 100%)`,
            border: `1px solid ${currentTheme.color}60`
          }}
        >
          <Icon size={12} style={{ color: currentTheme.color }} />
        </div>
        <span className={isOpen ? 'text-gold-300' : 'text-gray-300'}>{currentTheme.name}</span>
        <CaretDown size={12} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <>
          <div 
            className="fixed w-56 rounded-xl overflow-hidden"
            style={{
              top: position.top,
              right: position.right,
              zIndex: 99999,
              background: 'linear-gradient(180deg, rgba(15, 28, 50, 0.99) 0%, rgba(10, 20, 38, 1) 100%)',
              border: '1px solid rgba(0, 220, 255, 0.3)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 30px rgba(0, 220, 255, 0.15)'
            }}
          >
            <div className="h-[2px] bg-gradient-to-r from-transparent via-gold-500 to-transparent" />
            
            <div className="p-2">
              {themes.map((t) => {
                const ThemeIcon = t.icon;
                const isActive = theme === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTheme(t.id);
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg transition-all hover:bg-white/5"
                    style={{
                      background: isActive ? `${t.color}15` : 'transparent',
                      border: isActive ? `1px solid ${t.color}40` : '1px solid transparent'
                    }}
                  >
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ 
                        background: `linear-gradient(135deg, ${t.color}30 0%, ${t.color}10 100%)`,
                        border: `1px solid ${t.color}50`
                      }}
                    >
                      <ThemeIcon size={16} style={{ color: t.color }} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-white">{t.name}</div>
                      <div className="text-[10px] text-gray-500">{t.description}</div>
                    </div>
                    {isActive && (
                      <Check size={16} style={{ color: t.color }} weight="bold" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          
          <div 
            className="fixed inset-0" 
            style={{ zIndex: 99998 }}
            onClick={() => setIsOpen(false)} 
          />
        </>,
        document.body
      )}
    </div>
  );
};

export default ThemeSelector;
